<script setup lang="ts">
import Shepherd from 'shepherd.js'

const tour = shallowRef<Shepherd.Tour>()

const rootEl = getCurrentInstance()?.appContext.app._container as HTMLElement

const defaultButtons = [
    {
        text: 'Close',
        label: 'close',
        action: cancel
    },
    {
        text: 'Previous',
        label: 'previous',
        disabled: isFirst,
        action: back
    },
    {
        text: 'Next',
        label: 'next',
        action: next,
        disabled: isLast
    },
    {
        text: 'Done',
        label: 'done',
        action: complete,
        disabled: () => !isLast()
    }
]

/**
 * Get the tutorial attribute.
 *
 * @param el
 * @param key
 * @param defaultValue
 */
function getTutorialAttribute<T>(el: Element, key: string, defaultValue?: T) {
    return (el.getAttribute(`data-tutorial-${key}`) ?? defaultValue) as T
}

/**
 * Get the list of elemments to include in the tutorial.
 */
function getTutorialElements() {
    return Array.from(rootEl.querySelectorAll('[data-tutorial-step]')).filter(
        (el): el is HTMLElement => el instanceof HTMLElement
    )
}

/**
 * Get each action to perform on each step.
 * @param step
 */
function getStepActions(step: `step-${number}` | 'complete' | 'cancel') {
    const attrName = `data-tutorial-on-${step}`

    return Array.from(rootEl.querySelectorAll(`[${attrName}]`)).map(el => {
        const event = el.getAttribute(attrName) as string
        return () => el.dispatchEvent(new Event(event))
    })
}

/**
 * Create a step.
 *
 * @param el
 */
function makeStep(el: HTMLElement) {
    const index = Number(getTutorialAttribute(el, 'step', 1))
    const title = getTutorialAttribute(el, 'title', '')
    const text = getTutorialAttribute(el, 'text', '')
    const attachTo = getTutorialAttribute<'bottom' | 'top' | 'left' | 'right'>(
        el,
        'attach-to',
        'bottom'
    )

    return {
        index,
        title,
        text,
        attachTo: {
            element: el,
            on: attachTo
        }
    }
}

/**
 * Add actions to a step.
 *
 * @param step
 * @param index
 */
function addActions(step: Shepherd.Step, index: number) {
    // @ts-expect-error fine
    const stepIndex = step.options.index
    const actions = getStepActions(`step-${stepIndex}`)

    if (actions.length === 0) return

    const nextButton = defaultButtons.find(({ label }) => label === 'next')

    if (!nextButton) return

    const buttons = [...defaultButtons]

    if (nextButton) {
        buttons[buttons.indexOf(nextButton)] = {
            ...nextButton,
            action: async () => {
                actions.forEach(action => action())
                await setSteps()
                show(index + 1)
            }
        }
    }

    setHasAction(step, true)

    step.updateStepOptions({ buttons })
}

/**
 * Get the list of steps.
 */
function getSteps() {
    return getTutorialElements()
        .map(makeStep)
        .sort((a, b) => a.index - b.index)
}

/**
 * Set the list of steps.
 */
async function setSteps() {
    const instance = unref(tour)

    if (!instance) return

    await nextTick()

    removeAllElements()
    instance.steps = []
    instance.addSteps(getSteps())
    instance.steps.forEach(addActions)
}

/**
 * Show a specific step.
 *
 * @param index
 */
function show(index: number) {
    const instance = unref(tour)

    if (!instance) return

    const stepKey = instance.steps[index]?.id

    if (!stepKey) return

    instance.show(stepKey)
}

/**
 * Start the tutorial.
 */
async function start() {
    const instance = unref(tour)

    if (!instance) return

    await setSteps()

    instance.start()
}

/**
 * Go back one step.
 */
function back() {
    const instance = unref(tour)

    if (!instance) return

    instance.back()
}

/**
 * Go to the next step.
 */
function next() {
    const instance = unref(tour)

    if (!instance) return

    instance.next()
}

/**
 * Cancel the tutorial.
 */
function cancel() {
    const instance = unref(tour)

    if (!instance) return

    instance.cancel()
}

/**
 * Complete the tutorial.
 */
function complete() {
    const instance = unref(tour)

    if (!instance) return

    instance.complete()
}

/**
 * Get the current step.
 */
function currentStep() {
    const instance = unref(tour)

    if (!instance) return

    return instance.getCurrentStep()
}

/**
 * Get the current step index.
 */
function stepIndex(step: Shepherd.Step) {
    const instance = unref(tour)

    if (!instance) return 0

    return instance.steps.indexOf(step)
}

/**
 * Check if the current step is the last.
 */
function isLast(step = currentStep()) {
    if (!step) return false

    const instance = unref(tour)

    if (!instance) return false

    return !hasAction(step) && stepIndex(step) === instance.steps.length - 1
}

/**
 * Check if the current step is the first.
 */
function isFirst(step = currentStep()) {
    if (!step) return false

    return stepIndex(step) === 0
}

/**
 * Check if a step has an action.
 */
function hasAction(step?: Shepherd.Step) {
    // @ts-expect-error fine
    return step?.options.hasAction
}

/**
 * Set has action prop on a step.
 */
function setHasAction(step: Shepherd.Step, value: boolean) {
    // @ts-expect-error fine
    return (step.options.hasAction = value)
}

/**
 * Remove all step elements.
 */
function removeAllElements() {
    const instance = unref(tour)

    if (!instance) return

    instance.steps.forEach(step => {
        const el = step.getElement()

        if (!el) return

        el.remove()
    })
}

/**
 * Initialize the tutorial.
 */
function initialize() {
    tour.value = new Shepherd.Tour({
        useModalOverlay: true,
        defaultStepOptions: {
            scrollTo: true,
            buttons: defaultButtons
        },
        exitOnEsc: true
    })

    tour.value.on('complete', () => {
        getStepActions('complete').forEach(action => action())
        removeAllElements()
    })

    tour.value.on('cancel', () => {
        getStepActions('cancel').forEach(action => action())
        removeAllElements()
    })
}

onMounted(initialize)
</script>
    
<template>
    <button type="button" @click="start()">
        <InformationCircleIcon class="h-full" />
    </button>
</template>
    
<style>
body:has(.shepherd-element) *:not([class*='shepherd']) {
    @apply pointer-events-none;
}

.shepherd-modal-is-visible {
    @apply pointer-events-none fixed inset-0 z-[999] h-full w-full overflow-hidden opacity-75;
}
.shepherd-arrow {
    @apply h-8 w-8;
}

.shepherd-element {
    @apply pointer-events-auto z-[9999] max-w-md outline-none;
}

.shepherd-content {
    @apply rounded bg-black p-5 shadow;
}

[data-popper-placement='top'] > .shepherd-arrow {
    @apply top-full -mt-6;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="black" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>');
}

[data-popper-placement='top'] > .shepherd-content {
    @apply mb-3;
}

[data-popper-placement='bottom'] > .shepherd-arrow {
    @apply -mt-2;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="black" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" /></svg>');
}

[data-popper-placement='bottom'] > .shepherd-content {
    @apply mt-3;
}

[data-popper-placement='left'] > .shepherd-arrow {
    @apply -mr-2;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="black" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>');
}

[data-popper-placement='left'] > .shepherd-content {
    @apply ml-3;
}

[data-popper-placement='right'] > .shepherd-arrow {
    @apply -ml-2;
    background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" fill="black"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>');
}

[data-popper-placement='right'] > .shepherd-content {
    @apply mr-3;
}

.shepherd-text {
    @apply text-left text-base text-white;
}

.shepherd-footer {
    @apply mt-6 flex justify-end gap-4;
}

.shepherd-button {
    @apply bg-neutral border-neutral rounded border px-4 py-1 text-xs text-black outline-none hover:bg-black hover:text-white focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black;
}

.shepherd-button[aria-label='close'] {
    @apply bg-earth-red border-0 bg-opacity-50 text-white hover:bg-opacity-100;
}

.shepherd-button:disabled {
    @apply hidden;
}
</style>